import { useEffect, useState } from 'preact/hooks';
import OnboardingEgyptLuckyV1 from '../../components/Onboarding/OnboardingEgyptLuckyV1/OnboardingEgyptLuckyV1';
import { renderIf } from '../../utils/render.utils';
import LoadingScreen from '../../components/shared/LoadingScreen/LoadingScreen';
import OnboardingExitModal from '../../components/Onboarding/OnboardingExitModal/OnboardingExitModal';
import OnboardingService from '../../services/onboarding.service';
import NavigationUtils from '../../utils/navigation.utils';
import OnboardingStatusUrlUtils, {
  UserApplicationReason,
  UserApplicationStatus,
} from '../../utils/onboarding-status-url.utils';
import { TrackingEvent } from '../../sdk/tracking.event';
import SettingsService from '../../services/settings.service';

const DefaultSettings = {
  country: 'egypt',
  client: 'lucky',
  version: 'v1',
};

const OnboardingForm = {
  [`${DefaultSettings.country}-${DefaultSettings.client}-${DefaultSettings.version}`]:
    OnboardingEgyptLuckyV1,
};

const OnboardingVariant = ({ settings, ...props }) => {
  const key = `${settings.country}-${settings.client}-${settings.version}`;

  const Form = OnboardingForm[key];

  if (!Form) {
    return NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
  }

  return Form && <Form {...props} />;
};

export default function OnboardingPage({ popup, organization }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [isPersonalized, setPersonalized] = useState(false);

  useEffect(() => {
    if (!organization) {
      return NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
    }

    Promise.all([
      SettingsService.getOnboarding(organization).catch(e => {
        console.error(
          `Something went wrong during settings onboarding fetch. ${e}`,
        );
      }),
      OnboardingService.getStatus(organization),
    ])
      .then(([settingsResponse, statusResponse]) => {
        const { data: settings } = settingsResponse;
        const { data } = statusResponse;
        if (!data.needsOcr && data.needsOnboarding) {
          const userApplication = (data.userApplications || []).find(
            userApplication =>
              userApplication.organizationCode === organization,
          );

          setPersonalized(userApplication?.personalizedCard);

          return OnboardingService.getData().then(body => {
            setData(body.data);

            setLoading(false);

            // trigger tracking event
            TrackingEvent.trigger(
              TrackingEvent.Events.ONBOARDING_START_SHOW,
              TrackingEvent.Sources.CAAS,
              {},
            );
          });
        }

        const userApplication = (data.userApplications || []).find(
          userApplication => userApplication.organizationCode === organization,
        );

        const credit =
          settings?.lendingButtonEnabled && data?.credit ? data?.credit : {};

        const url = OnboardingStatusUrlUtils.getUrl(
          data.userIdentity,
          userApplication,
          credit,
          settings?.automaticLendingEnabled,
          settings?.personalizedCardEnabled,
        );

        const params = OnboardingStatusUrlUtils.getParams({
          credit,
          organizationCode: organization,
          userApplication,
          automaticLendingEnabled: settings?.automaticLendingEnabled,
        });

        NavigationUtils.navigate(url, false, params);
      })
      .catch(err => {
        console.error(`Something went wrong`, err);
        if (err?.body?.code === 'USER_NOT_INVITED_ERROR') {
          const url = OnboardingStatusUrlUtils.getUrl(
            {},
            {
              status: UserApplicationStatus.REJECTED,
              reason: UserApplicationReason.INFORMATION_MISMATCH,
            },
          );
          return NavigationUtils.navigate(url, false);
        }
        NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
      });
  }, []);

  const handleFormSubmit = formData => {
    setLoading(true);

    OnboardingService.saveForm(organization, formData)
      .then(() => {
        TrackingEvent.trigger(
          TrackingEvent.Events.ONBOARDING_START_SHOW,
          TrackingEvent.Sources.CAAS,
          {},
        );
        return Promise.all([
          SettingsService.getOnboarding(organization).catch(e => {
            console.error(
              `Something went wrong during settings onboarding fetch. ${e}`,
            );
          }),
          OnboardingService.getStatus(organization),
        ]);
      })
      .then(([settingsResponse, statusResponse]) => {
        const { data: settings } = settingsResponse;
        const { data } = statusResponse;

        const userApplication = (data.userApplications || []).find(
          userApplication => userApplication.organizationCode === organization,
        );

        const credit =
          settings?.lendingButtonEnabled && data?.credit ? data?.credit : {};

        const url = OnboardingStatusUrlUtils.getUrl(
          data.userIdentity,
          userApplication,
          credit,
          settings?.automaticLendingEnabled,
          settings?.personalizedCardEnabled,
        );

        const params = OnboardingStatusUrlUtils.getParams({
          credit,
          organizationCode: organization,
          userApplication,
          automaticLendingEnabled: settings?.automaticLendingEnabled,
        });

        NavigationUtils.navigate(url, false, params);
      })
      .catch(err => {
        console.error(`Something went wrong`, err);
        if (err?.body?.code === 'USER_NOT_INVITED_ERROR') {
          const url = OnboardingStatusUrlUtils.getUrl(
            {},
            {
              status: UserApplicationStatus.REJECTED,
              reason: UserApplicationReason.INFORMATION_MISMATCH,
            },
          );
          return NavigationUtils.navigate(url, false);
        }
        NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
      });
  };

  const showPopup = () => {
    NavigationUtils.navigate(NavigationUtils.Routes.ONBOARDING, false, {
      popup: true,
    });
  };

  return (
    <>
      {renderIf(loading, <LoadingScreen />)}
      {renderIf(
        !loading,
        <>
          <OnboardingVariant
            settings={DefaultSettings}
            data={data}
            onCloseClick={showPopup}
            onBackClick={showPopup}
            onFormSubmit={handleFormSubmit}
            isPersonalized={isPersonalized}
          />

          <OnboardingExitModal
            showing={!!popup}
            onCloseClick={() => {
              NavigationUtils.navigate(NavigationUtils.Routes.ONBOARDING, true);
            }}
          />
        </>,
      )}
    </>
  );
}
